import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { splitName } from "./NameInputs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Upload, Circle, ChevronRight, Lock } from "lucide-react";

const VISA_TYPES = ["Australian Citizen", "Permanent Resident", "Subclass 482 (TSS)", "Subclass 485 (Graduate)", "Subclass 500 (Student)", "Subclass 417/462 (Working Holiday)", "Subclass 491/494 (Regional)", "Bridging Visa", "Other / N/A"];

// Required onboarding documents to upload — each is ticked once uploaded.
const REQUIRED_DOCS = [
  ["id", "Passport or ID"],
  ["right_to_work", "Visa / right-to-work document"],
];

// Module-level field so inputs keep focus between keystrokes.
function MField({ testid, label, value, type, onChange, inputMode }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-600">{label}</Label>
      <Input data-testid={testid} type={type || "text"} inputMode={inputMode} value={value || ""} onChange={onChange}
        className="h-12 mt-1.5 text-base rounded-xl" />
    </div>
  );
}

export default function HrOnboard() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({});
  const [uploaded, setUploaded] = useState({});
  const [busyDoc, setBusyDoc] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    hrApi.get(`/public/link/${token}`).then((r) => {
      setInfo(r.data); if (r.data.status === "completed") setDone(true);
      const p = r.data.prefill || {}; const s = splitName(p.name);
      setForm({ ...p, first_name: p.first_name || s.first, family_name: p.family_name || s.last });
    })
      .catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  }, [token]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upload = async (type, file) => {
    if (!file) return; setBusyDoc(type);
    const fd = new FormData(); fd.append("file", file);
    try { await hrApi.post(`/public/onboarding/${token}/document?doc_type=${type}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setUploaded((u) => ({ ...u, [type]: file.name })); toast.success("Uploaded ✓"); }
    catch { toast.error("Upload failed"); } setBusyDoc(null);
  };
  const submit = async () => {
    if (!form.first_name?.trim() || !form.family_name?.trim()) return toast.error("Please enter your first and last name");
    if (!form.mobile?.trim()) return toast.error("Please enter your mobile number");
    if (!form.email?.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please enter a valid email address");
    if (!ack) return toast.error("Please acknowledge the induction");
    setBusy(true);
    try { await hrApi.post(`/public/onboarding/${token}/submit`, { fields: form, induction_acknowledged: ack }); setDone(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  const doneCount = REQUIRED_DOCS.filter(([t]) => uploaded[t]).length;

  if (err) return <Shell><div className="text-center text-slate-500 py-6" data-testid="onboard-invalid">{err}</div></Shell>;
  if (!info) return <Shell><div className="text-center text-slate-400 py-6">Loading…</div></Shell>;
  if (done) return <Shell><div className="text-center space-y-3 py-6" data-testid="onboard-done">
    <div className="w-16 h-16 rounded-full bg-teal-100 grid place-items-center mx-auto"><CheckCircle2 className="text-teal-600" size={32} /></div>
    <h1 className="font-heading font-extrabold text-2xl text-slate-800">All done!</h1>
    <p className="text-slate-500">Thanks — your details have been sent to HR. Welcome aboard.</p>
  </div></Shell>;

  return (
    <Shell>
      <div data-testid="onboard-page" className="space-y-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-slate-800">Welcome — let's get you set up</h1>
          <p className="text-slate-500 text-sm mt-1">Fill in your details, upload your documents, and confirm the induction. It only takes a few minutes.</p>
        </div>

        {/* Your details — single column, large targets */}
        <section className="space-y-4">
          <h3 className="font-heading font-bold text-slate-700 text-lg">Your details</h3>
          <MField testid="onb-first_name" label="First name *" value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} />
          <MField testid="onb-family_name" label="Last name *" value={form.family_name} onChange={(e) => setF("family_name", e.target.value)} />
          <MField testid="onb-preferred_name" label="Preferred name" value={form.preferred_name} onChange={(e) => setF("preferred_name", e.target.value)} />
          <MField testid="onb-dob" label="Date of birth" type="date" value={form.dob} onChange={(e) => setF("dob", e.target.value)} />
          <MField testid="onb-mobile" label="Mobile *" type="tel" inputMode="tel" value={form.mobile} onChange={(e) => setF("mobile", e.target.value)} />
          <MField testid="onb-email" label="Personal email *" type="email" inputMode="email" value={form.email} onChange={(e) => setF("email", e.target.value)} />
          <MField testid="onb-address" label="Home address" value={form.address} onChange={(e) => setF("address", e.target.value)} />
          <MField testid="onb-emergency_contact" label="Emergency contact (name & phone)" value={form.emergency_contact} onChange={(e) => setF("emergency_contact", e.target.value)} />
          <div>
            <Label className="text-sm font-medium text-slate-600">Visa / work status</Label>
            <Select value={form.visa_type || ""} onValueChange={(v) => setF("visa_type", v)}>
              <SelectTrigger data-testid="onb-visa_type" className="h-12 mt-1.5 text-base rounded-xl"><SelectValue placeholder="Select your status…" /></SelectTrigger>
              <SelectContent>{VISA_TYPES.map((v) => <SelectItem key={v} value={v} className="text-base py-2.5">{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </section>

        {/* Payroll, tax & bank — fillable, HR-only */}
        <section className="space-y-4">
          <h3 className="font-heading font-bold text-slate-700 text-lg">Payroll, tax &amp; bank details</h3>
          <p className="text-sm text-slate-500 -mt-2 inline-flex items-center gap-1.5"><Lock size={13} className="text-slate-400" /> Kept private &amp; secure — visible only to HR.</p>
          <MField testid="onb-tfn" label="Tax File Number (TFN)" inputMode="numeric" value={form.tfn} onChange={(e) => setF("tfn", e.target.value)} />
          <MField testid="onb-super_fund" label="Super fund name" value={form.super_fund} onChange={(e) => setF("super_fund", e.target.value)} />
          <MField testid="onb-super_abn" label="Super fund ABN" inputMode="numeric" value={form.super_abn} onChange={(e) => setF("super_abn", e.target.value)} />
          <MField testid="onb-super_member_no" label="Super member number" value={form.super_member_no} onChange={(e) => setF("super_member_no", e.target.value)} />
          <MField testid="onb-bank_account_name" label="Bank account name" value={form.bank_account_name} onChange={(e) => setF("bank_account_name", e.target.value)} />
          <MField testid="onb-bank_bsb" label="BSB" inputMode="numeric" value={form.bank_bsb} onChange={(e) => setF("bank_bsb", e.target.value)} />
          <MField testid="onb-bank_account_no" label="Account number" inputMode="numeric" value={form.bank_account_no} onChange={(e) => setF("bank_account_no", e.target.value)} />
        </section>

        {/* Document checklist */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-slate-700 text-lg">Documents to upload</h3>
            <span data-testid="onb-doc-progress" className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">{doneCount} of {REQUIRED_DOCS.length}</span>
          </div>
          <p className="text-sm text-slate-500 -mt-1">Tap each item to upload a photo or PDF. It ticks off once received.</p>
          <div className="space-y-2.5">
            {REQUIRED_DOCS.map(([t, l]) => {
              const isDone = !!uploaded[t];
              const isBusy = busyDoc === t;
              return (
                <label key={t} data-testid={`onb-doc-${t}`} className={`flex items-center gap-3 rounded-2xl border-2 p-4 min-h-[64px] cursor-pointer transition-colors ${isDone ? "border-teal-300 bg-teal-50" : "border-slate-200 bg-white active:bg-slate-50"}`}>
                  {isDone ? <CheckCircle2 size={26} className="text-teal-600 shrink-0" data-testid={`onb-check-${t}`} /> : <Circle size={26} className="text-slate-300 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-slate-800 leading-tight">{l}</div>
                    <div className={`text-xs mt-0.5 truncate ${isDone ? "text-teal-600" : "text-slate-400"}`}>{isBusy ? "Uploading…" : isDone ? `Uploaded · ${uploaded[t]}` : "Required · tap to upload"}</div>
                  </div>
                  {isDone ? <span className="text-xs font-semibold text-teal-600 shrink-0">Change</span> : <span className="inline-flex items-center gap-1 text-indigo-600 font-semibold text-sm shrink-0"><Upload size={16} /> Upload</span>}
                  <input type="file" accept="image/*,application/pdf" className="hidden" disabled={isBusy} onChange={(e) => { upload(t, e.target.files[0]); e.target.value = ""; }} />
                </label>
              );
            })}
          </div>
        </section>

        {/* Induction */}
        <section className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <h3 className="font-heading font-bold text-slate-700 text-lg mb-1">Induction</h3>
          <p className="text-sm text-slate-600 mb-3">I confirm I have received and understood the workplace induction, including safety, hygiene and site rules.</p>
          <label className="flex items-center gap-3 text-base text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="onb-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="w-6 h-6 accent-indigo-500 shrink-0" />
            I acknowledge the induction
          </label>
        </section>

        <Button data-testid="onb-submit" onClick={submit} disabled={busy} className="w-full h-14 bg-indigo-500 hover:bg-indigo-400 text-white rounded-2xl font-bold text-base inline-flex items-center justify-center gap-2">
          {busy ? "Submitting…" : <>Finish onboarding <ChevronRight size={20} /></>}
        </Button>
        <p className="text-xs text-slate-400 text-center pb-2">Your submission time and IP address are recorded for HR records.</p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-100 py-6 px-4"><div className="max-w-md mx-auto bg-white rounded-3xl shadow-sm p-5 sm:p-6">{children}</div></div>;
}
